import { Injectable } from '@angular/core';
import {
   AuthActions,
   AuthService,
   OAuthLibWrapperService,
   OCC_USER_ID_CURRENT,
   RoutingService,
   UserIdService,
   WindowRef,
} from '@spartacus/core';
import { AsmAuthStorageService, CsAgentAuthService, TokenTarget } from '@spartacus/asm/root';
import { Store } from '@ngrx/store';
import { ASM } from '@siko/constants';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
   providedIn: 'root',
})
export class SikoCsAgentAuthService extends CsAgentAuthService {
   constructor(
      readonly routingService: RoutingService,
      readonly winRef: WindowRef,
      authService: AuthService,
      authStorageService: AsmAuthStorageService,
      userIdService: UserIdService,
      oAuthLibWrapperService: OAuthLibWrapperService,
      store: Store,
      userAccountFacade: UserAccountFacade
   ) {
      super(authService, authStorageService, userIdService, oAuthLibWrapperService, store, userAccountFacade);
      this.trackAgentInLocalStorage();
   }

   private trackAgentInLocalStorage(): void {
      combineLatest([this.authStorageService.getToken(), this.authStorageService.getTokenTarget()])
         .pipe(
            map(([token, tokenTarget]) =>
               tokenTarget === TokenTarget.CSAgent ? this.decodeTokenSubject(token?.access_token) : undefined
            ),
            distinctUntilChanged(),
            takeUntilDestroyed()
         )
         .subscribe((agentUid: string | undefined) => {
            if (agentUid) {
               this.winRef.localStorage?.setItem(ASM.ASM_AGENT, agentUid);
            } else {
               this.winRef.localStorage?.removeItem(ASM.ASM_AGENT);
            }
         });
   }

   private decodeTokenSubject(accessToken?: string): string | undefined {
      const payload = accessToken?.split('.')[1];

      if (!payload) {
         return undefined;
      }

      try {
         const base64 = payload.replace(/-/gu, '+').replace(/_/gu, '/');
         const claims: { sub?: string } = JSON.parse(atob(base64));

         return claims.sub;
      } catch {
         return undefined;
      }
   }

   async logoutCustomerSupportAgent(): Promise<void> {
      const emulatedToken = this.authStorageService.getEmulatedUserToken();

      let isCustomerEmulated;

      this.userIdService
         .isEmulated()
         .subscribe(emulated => (isCustomerEmulated = emulated))
         .unsubscribe();

      await this.oAuthLibWrapperService.revokeAndLogout();

      this.store.dispatch({ type: '[Auth] Logout Customer Support Agent' });
      this.authStorageService.setTokenTarget(TokenTarget.User);

      if (isCustomerEmulated && emulatedToken) {
         this.store.dispatch(new AuthActions.Logout());
         this.authStorageService.setToken(emulatedToken);
         this.userIdService.setUserId(OCC_USER_ID_CURRENT);
         this.authStorageService.clearEmulatedUserToken();
         this.store.dispatch(new AuthActions.Login());
      } else {
         this.oAuthLibWrapperService.refreshAuthConfig();
         this.authService.logout();
      }
   }

   startCustomerEmulationSession(customerId: string): void {
      void this.routingService.go('/');

      super.startCustomerEmulationSession(customerId);
   }
}
